import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { XIcon } from "lucide-react";
import { useUpdateUserTicketMutation } from "../../api/marathonAdmin";
import { useNotification } from "../../hooks/useNotification";

interface MarathonTicketEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    marathonId: number;
    ticket: any;
}

/**
 * Преобразует Unix timestamp в формат для input type="datetime-local" (YYYY-MM-DDTHH:mm)
 */
const timestampToDatetimeLocal = (timestamp: number | null | undefined): string => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Преобразует формат datetime-local обратно в Unix timestamp
 */
const datetimeLocalToTimestamp = (datetime: string): number => {
    if (!datetime) return 0;
    return Math.floor(new Date(datetime).getTime() / 1000);
};

export const MarathonTicketEditModal: React.FC<MarathonTicketEditModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    marathonId,
    ticket,
}) => {
    const { showSuccess, showError } = useNotification();
    const [updateUserTicket, { isLoading }] = useUpdateUserTicketMutation();
    const [formData, setFormData] = useState<any>({});

    useEffect(() => {
        if (ticket) {
            setFormData({
                ...ticket,
                // Конвертируем метки времени для отображения в инпутах
                alny_marafone_tickets_time_dt: timestampToDatetimeLocal(ticket.alny_marafone_tickets_time),
                alny_marafone_tickets_paid_time_dt: timestampToDatetimeLocal(ticket.alny_marafone_tickets_paid_time),
                alny_marafone_tickets_delete_time_dt: timestampToDatetimeLocal(ticket.alny_marafone_tickets_delete_time),
            });
        }
    }, [ticket]);

    if (!isOpen || !ticket) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setFormData((prev: any) => ({ 
            ...prev, 
            [name]: type === 'number' ? Number(value) : value 
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            // Подготавливаем данные для отправки (конвертируем даты обратно в timestamp)
            const submissionData = { ...formData };
            submissionData.alny_marafone_tickets_time = datetimeLocalToTimestamp(formData.alny_marafone_tickets_time_dt);
            submissionData.alny_marafone_tickets_paid_time = datetimeLocalToTimestamp(formData.alny_marafone_tickets_paid_time_dt);
            submissionData.alny_marafone_tickets_delete_time = datetimeLocalToTimestamp(formData.alny_marafone_tickets_delete_time_dt);
            
            // Удаляем временные поля для инпутов
            delete submissionData.alny_marafone_tickets_time_dt;
            delete submissionData.alny_marafone_tickets_paid_time_dt;
            delete submissionData.alny_marafone_tickets_delete_time_dt;

            await updateUserTicket({
                ticketId: ticket.alny_marafone_tickets_id,
                data: submissionData,
                marathonId,
            }).unwrap();
            
            showSuccess("Билет успешно обновлен");
            if (onSuccess) onSuccess();
            onClose();
        } catch (error: any) {
            showError(error?.data?.message || error?.message || "Ошибка при обновлении билета");
        }
    };

    return (
        <div className="fixed inset-0 z-[60] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                                Редактирование билета (ID: {ticket.alny_marafone_tickets_id})
                            </h3>
                            <button onClick={onClose} className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                <span className="sr-only">Закрыть</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Победитель (ID участника)</label>
                                    <input type="number" name="alny_marafone_tickets_choosen_id" value={formData.alny_marafone_tickets_choosen_id ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">ID пользователя (Владелец)</label>
                                    <input type="number" name="alny_marafone_tickets_user_id" value={formData.alny_marafone_tickets_user_id ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Статус</label>
                                    <select 
                                        name="alny_marafone_tickets_status" 
                                        value={formData.alny_marafone_tickets_status ?? 0} 
                                        onChange={handleChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    >
                                        <option value={0}>Рабочий</option>
                                        <option value={1}>Билет победил</option>
                                        <option value={2}>Удален (отмена участника)</option>
                                        <option value={3}>Не победил (владелец победил другим)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">ID марафона</label>
                                    <input type="number" name="alny_marafone_tickets_number" value={formData.alny_marafone_tickets_number ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Дата создания</label>
                                    <input 
                                        type="datetime-local" 
                                        name="alny_marafone_tickets_time_dt" 
                                        value={formData.alny_marafone_tickets_time_dt ?? ''} 
                                        onChange={handleChange} 
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Дата последней выплаты</label>
                                    <input 
                                        type="datetime-local" 
                                        name="alny_marafone_tickets_paid_time_dt" 
                                        value={formData.alny_marafone_tickets_paid_time_dt ?? ''} 
                                        onChange={handleChange} 
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">За кого получен (ID)</label>
                                    <input type="number" name="alny_marafone_tickets_for" value={formData.alny_marafone_tickets_for ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">ID билета за кого (for_id)</label>
                                    <input type="number" name="alny_marafone_tickets_for_id" value={formData.alny_marafone_tickets_for_id ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Дата аннулирования</label>
                                    <input 
                                        type="datetime-local" 
                                        name="alny_marafone_tickets_delete_time_dt" 
                                        value={formData.alny_marafone_tickets_delete_time_dt ?? ''} 
                                        onChange={handleChange} 
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
                                    />
                                </div>
                            </div>
                            <div className="pt-4 flex justify-end gap-3">
                                <button type="button" onClick={onClose} className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none">
                                    Отмена
                                </button>
                                <button type="submit" disabled={isLoading} className="inline-flex justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none disabled:opacity-50">
                                    {isLoading ? "Сохранение..." : "Сохранить"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};
